<template>
  <div class="bg-green-200 p-4 rounded-md" v-if="ohrData">
    <h3 class="text-lg font-semibold mb-2">{{ capitalizedSide }} Ohr</h3>
    
    <!-- Buttons to open AudiogramChart and VerstaendlichkeitChart modals -->
    <div class="flex mb-4">
      <button @click="showAudiogramModal = true" class="w-1/2 px-4 py-2 bg-blue-500 text-white rounded-l hover:bg-blue-600">
        Audiogramm öffnen
      </button>
      <button @click="showVerstaendlichkeitModal = true" class="w-1/2 px-4 py-2 bg-blue-500 text-white rounded-r hover:bg-blue-600 ml-1">
        Verständlichkeit öffnen
      </button>
    </div>

    <!-- AudiogramChart Modal -->
    <AudiogramChart
      v-if="showAudiogramModal"
      :side="side"
      :isVisible="showAudiogramModal"
      @close="showAudiogramModal = false"
    />

    

  <!-- VerstaendlichkeitChart Modal -->
  <VerstaendlichkeitChart
    v-if="showVerstaendlichkeitModal"
    :side="side"
    :isVisible="showVerstaendlichkeitModal"
    @close="showVerstaendlichkeitModal = false"
  />


    <!-- Impedanz -->
    <div class="mb-4">
      <h4 class="text-md font-semibold mb-2">Impedanz</h4>

    <!-- Sondenohr -->
<div class="mb-2 border border-gray-300 rounded p-4">
  <h5 class="text-sm font-semibold mb-1">Sondenohr</h5>
    
  <!-- moDruckH2O -->
  <div class="mb-2 flex items-center">
    <span class="text-xs mr-2">moDruckH2O:</span>
    <input
      :id="`moDruckH2O-${side}`"
      v-model="ohrData.moDruckH2O"
      type="number"
      placeholder="moDruckH2O"
      class="p-2 border rounded w-24"
      @input="updateField('moDruckH2O', null, $event.target.value)"
    />
  </div>

  <!-- Stapediusreflex -->
  <div class="grid grid-cols-2 gap-2 mb-2">
    <div class="flex flex-col">
      <h6 class="text-xs font-semibold">SL</h6>
      <div v-for="freq in stapediusFrequencies" :key="freq" class="flex items-center justify-between">
        <span class="text-xs">{{ freq }}:</span>
        <input
          :id="`stapediusreflex-${side}-SL-${freq}`"
          v-model="ohrData.stapediusreflex.SL[freq]"
          type="number"
          :placeholder="`SL ${freq}`"
          class="p-2 border rounded w-24"
          @input="updateField('stapediusreflex', `SL.${freq}`, $event.target.value)"
        />
      </div>
    </div>
    <div class="flex flex-col">
      <h6 class="text-xs font-semibold">HL</h6>
      <div v-for="freq in stapediusFrequencies" :key="freq" class="flex items-center justify-between">
        <span class="text-xs">{{ freq }}:</span>
        <input
          :id="`stapediusreflex-${side}-HL-${freq}`"
          v-model="ohrData.stapediusreflex.HL[freq]"
          type="number"
          :placeholder="`HL ${freq}`"
          class="p-2 border rounded w-24"
          @input="updateField('stapediusreflex', `HL.${freq}`, $event.target.value)"
        />
      </div>
    </div>
  </div>
</div>

      <!-- Hörweite -->
  <div class="grid grid-cols-2 gap-2 mb-2">
    <div class="flex flex-col">
      <label :for="`hoorweite-${side}-FlSpr`" class="text-sm">Hörweite Fl. Spr.</label>
      <select
        :id="`hoorweite-${side}-FlSpr`"
        v-model="ohrData.hoorweite.FlSpr"
        class="p-2 border rounded"
        @change="updateField('hoorweite', 'FlSpr', $event.target.value)"
      >
        <option value="">Bitte auswählen</option>
        <option value="4-6 (gering)">4-6 (gering)</option>
        <option value="1-4 (mittel)">1-4 (mittel)</option>
        <option value="0-1 (hoch)">0-1 (hoch)</option>
        <option value="0 (taub)">0 (taub)</option>
      </select>
    </div>
    <div class="flex flex-col">
      <label :for="`hoorweite-${side}-UmSpr`" class="text-sm">Hörweite Um. Spr.</label>
      <select
        :id="`hoorweite-${side}-UmSpr`"
        v-model="ohrData.hoorweite.UmSpr"
        class="p-2 border rounded"
        @change="updateField('hoorweite', 'UmSpr', $event.target.value)"
      >
        <option value="">Bitte auswählen</option>
        <option value="4-X (gering)">4-X (gering)</option>
        <option value="1-4 (mittel)">1-4 (mittel)</option>
        <option value="0.25-1 (hoch)">0.25-1 (hoch)</option>
        <option value="0-0.25 (taub)">0-0.25 (taub)</option>
      </select>
    </div>
  </div>

      
    </div>


    <!-- Gehörgang und Trommelfell -->
  <div class="mb-4 flex items-center">
    <div class="w-1/2 pr-2">
      <h5 class="text-sm font-semibold mb-1">Gehörgang</h5>
      <select
        :id="`gehoergang`"
        v-model="gehoergangStatus"
        class="p-2 border rounded w-full"
        @change="updateGehoergangStatus($event.target.value)"
      >
        <option value="normal">Normal</option>
        <option value="erweitert">Erweitert</option>
        <option value="eng">Eng</option>
      </select>
    </div>
    <div class="w-1/2 pl-2">
      <h5 class="text-sm font-semibold mb-1">Trommelfell</h5>
      <select
        :id="`trommelfell`"
        v-model="trommelfellStatus"
        class="p-2 border rounded w-full"
        @change="updateTrommelfellStatus($event.target.value)"
      >
        <option value="normal">Normal</option>
        <option value="durchloechert">Durchlöchert</option>
        <option value="feucht">Feucht</option>
        <option value="durchloechert-feucht">Durchlöchert und feucht</option>
      </select>
    </div>
  </div>

    <!-- Hörhilfe notwendig -->
    <div class="mb-4 flex items-center">
      <input
        :id="`hoerhilfeNotwendig-${side}`"
        v-model="ohrData.hoerhilfeNotwendig"
        type="checkbox"
        class="mr-2"
        @change="updateField('hoerhilfeNotwendig', null, $event.target.checked)"
      />
      <label :for="`hoerhilfeNotwendig-${side}`">Hörhilfe notwendig</label>
    </div>

    <!-- Tinnitushilfe -->
    <div class="mb-4">
      <h4 class="text-md font-semibold mb-2">Tinnitushilfe</h4>
      <div class="grid grid-cols-2 gap-2">
        <div v-for="(label, key) in tinnitushilfeOptions" :key="key" class="flex flex-col">
          <label :for="`tinnitushilfe-${key}-${side}`" class="text-sm">{{ label }}</label>
          <input
            :id="`tinnitushilfe-${key}-${side}`"
            v-model="ohrData.tinnitushilfe[key]"
            type="number"
            :placeholder="label"
            class="p-2 border rounded"
            @input="updateField('tinnitushilfe', key, $event.target.value)"
          />
        </div>
      </div>
      <div class="mt-2 flex items-center">
        <input
          :id="`tinnitushilfe-notwendig-${side}`"
          v-model="ohrData.tinnitushilfe.notwendig"
          type="checkbox"
          class="mr-2"
          @change="updateField('tinnitushilfe', 'notwendig', $event.target.checked)"
        />
        <label :for="`tinnitushilfe-notwendig-${side}`">Tinnitushilfe notwendig</label>
      </div>
    </div>
  </div>
  <div>
    <div class="mt-4">
      <button
        @click="$emit('close')"
        class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        Schließen
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useERezeptStore } from '../../stores/eRezeptStore';
import AudiogramChart from './AudiogramChart.vue';
import VerstaendlichkeitChart from './VerstaendlichkeitChart.vue';

export default {
  name: 'HearingLossDataForm',
  components: {
    AudiogramChart,
    VerstaendlichkeitChart
  },
  props: {
    side: {
      type: String,
      required: true,
      validator: (value) => ['rechts', 'links'].includes(value),
    },
  },
  setup(props) {
    const eRezeptStore = useERezeptStore();
    const showAudiogramModal = ref(false);
    const showVerstaendlichkeitModal = ref(false);
    const capitalizedSide = computed(() => {
      const side = props.side;
      if (side === 'rechts') {
        return 'Rechtes';
      } else if (side === 'links') {
        return 'Linkes';
      } else {
        return side.charAt(0).toUpperCase() + side.slice(1);
      }
    });

    const ohrData = computed(() => eRezeptStore.muster15formData.ohren[props.side]);

    const gehoergangStatus = ref('normal');
    const trommelfellStatus = ref('normal');

    const updateGehoergangStatus = (status) => {
      gehoergangStatus.value = status;
      const ohrData = eRezeptStore.muster15formData.ohren[props.side];
      if (status === 'normal') {
        ohrData.gehoergang.erweitert = false;
        ohrData.gehoergang.eng = false;
      } else if (status === 'erweitert') {
        ohrData.gehoergang.erweitert = true;
        ohrData.gehoergang.eng = false;
      } else {
        ohrData.gehoergang.erweitert = false;
        ohrData.gehoergang.eng = true;
      }
    };

    const updateTrommelfellStatus = (status) => {
      trommelfellStatus.value = status;
      const ohrData = eRezeptStore.muster15formData.ohren[props.side];
      if (status === 'normal') {
        ohrData.trommelfell.durchloechert = false;
        ohrData.trommelfell.feucht = false;
      } else if (status === 'durchloechert') {
        ohrData.trommelfell.durchloechert = true;
        ohrData.trommelfell.feucht = false;
      } else if (status === 'feucht') {
        ohrData.trommelfell.durchloechert = false;
        ohrData.trommelfell.feucht = true;
      } else if (status === 'durchloechert-feucht') {
        ohrData.trommelfell.durchloechert = true;
        ohrData.trommelfell.feucht = true;
      }
    };

    onMounted(() => {
      // Initialisiere Audiogramm-Daten, falls sie noch nicht existieren
      const frequencies = ['0125KHz', '025KHz', '05KHz', '1KHz', '15KHz', '2KHz', '3KHz', '4KHz', '6KHz', '8KHz', '10KHz'];
      frequencies.forEach(freq => {
        if (!ohrData.value.hoerverlust[freq]) {
          eRezeptStore.updateMuster15OhrField(props.side, 'hoerverlust', freq, '');
        }
      });
    });

    const updateField = (category, field, value) => {
      eRezeptStore.updateMuster15OhrField(props.side, category, field, value);
    };

    const stapediusFrequencies = ['05KHz', '1KHz', '2KHz', '4KHz'];
    

    const checkboxOptions = {
      gehoergang: {
        erweitert: 'Gehörgang erweitert',
        eng: 'Gehörgang eng'
      },
      trommelfell: {
        durchloechert: 'Trommelfell durchlöchert',
        feucht: 'Trommelfell feucht'
      }
    };

    const hoorweiteOptions = {
      FlSpr: 'Hörweite Fl. Spr.',
      UmSpr: 'Hörweite Um. Spr.'
    };

    const tinnitushilfeOptions = {
      frequenz: 'Frequenz',
      verdeckbarLautstaerke: 'Verdeckbar Lautstärke'
    };

    return {
      eRezeptStore,
      ohrData,
      capitalizedSide,
      updateField,
      showAudiogramModal,
      stapediusFrequencies,
      showVerstaendlichkeitModal,
      checkboxOptions,
      hoorweiteOptions,
      tinnitushilfeOptions,
      gehoergangStatus,
      trommelfellStatus,
      updateGehoergangStatus,
      updateTrommelfellStatus
    };
  },
};
</script>